import { memo, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useGeoMarkupStore } from '@/store';
import {
  selectBasemapPreferenceKey,
  selectResolvedBasemapId,
  selectVisibleBasemapDefinitions,
} from '@/store/selectors';
import { BasemapCardGrid } from '@/components/ui/BasemapCardGrid';
import type { BasemapId } from '@/config/maplibre';

export const BasemapSwitcher = memo(function BasemapSwitcher() {
  const basemapId = useGeoMarkupStore(selectResolvedBasemapId);
  const setUserBasemapForWorkspace = useGeoMarkupStore(
    (state) => state.map.setUserBasemapForWorkspace
  );
  const is3DMode = useGeoMarkupStore((state) => state.map.is3DMode);
  // Allowlist: the share's basemap list on public pages, otherwise the
  // workspace config (falling back to all basemaps with country gating).
  const visibleBasemaps = useGeoMarkupStore(
    useShallow(selectVisibleBasemapDefinitions)
  );
  const preferenceKey = useGeoMarkupStore(selectBasemapPreferenceKey);

  const items = useMemo(
    () =>
      visibleBasemaps.map((basemap) => ({
        id: basemap.id,
        labelKey: basemap.labelKey,
        thumbnail: basemap.thumbnail,
      })),
    [visibleBasemaps]
  );

  // Picking a basemap persists the choice per workspace (or per share) for
  // this user; selectResolvedBasemapId derives the runtime style from it.
  const handleSelect = (id: BasemapId): void => {
    if (preferenceKey) {
      setUserBasemapForWorkspace(preferenceKey, id);
    }
  };

  return (
    <BasemapCardGrid
      mode="single"
      items={items}
      selectedIds={[basemapId]}
      onToggle={handleSelect}
      disabled={is3DMode}
      disabledTooltipKey="layers.basemap.disabled3D"
    />
  );
});
